import { initializeApp } from "firebase/app";
import { getFirestore, connectFirestoreEmulator, serverTimestamp, FieldValue } from "firebase/firestore"; // Added serverTimestamp and FieldValue
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";

const env = import.meta.env;

console.log("Firebase Init - Environment Mode:", env.MODE);
console.log("Firebase Init - API Key Loaded:", !!env.VITE_FIREBASE_API_KEY);
console.log("Firebase Init - Project ID:", env.VITE_FIREBASE_PROJECT_ID);

// Detect UAT environment to enforce the correct authDomain
const isUat = env.MODE === 'uat' || 
              env.VITE_APP_ENV === 'uat' || 
              (typeof window !== 'undefined' && window.location.hostname.includes('uat'));

// Load config from Vite Environment Variables (.env.development or .env.production)
const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: isUat ? 'uat.racelineiq.com' : env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.VITE_FIREBASE_APP_ID,
  measurementId: env.VITE_FIREBASE_MEASUREMENT_ID
};

// Validates that configuration exists
if (!firebaseConfig.apiKey) {
  console.error("CRITICAL: Firebase Config missing! API Key is undefined.");
  console.warn("Ensure you have a '.env.development' file in the project ROOT (next to package.json).");
  console.warn("The file content should start with: VITE_FIREBASE_API_KEY=...");
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);
// Initialize Compat for Auth
firebase.initializeApp(firebaseConfig);

// Export services
export const db = getFirestore(app);
export const storage = getStorage(app);
export const auth = firebase.auth();
export const functions = getFunctions(app, 'us-central1');
export { serverTimestamp, FieldValue }; // Re-export serverTimestamp and FieldValue

// Check if the app is currently running in a hosted environment (Firebase Hosting, custom domains)
const isHosted = typeof window !== 'undefined' && (
  window.location.hostname.includes('web.app') ||
  window.location.hostname.includes('firebaseapp.com') ||
  window.location.hostname.includes('racelineiq.com')
);

// Check if the app is currently loaded in a local browser or LAN IP
const isLocalhost = typeof window !== 'undefined' && 
                    !isHosted &&
                    (window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1' || 
                     window.location.hostname.endsWith('.local') ||
                     window.location.hostname.startsWith('192.168.') ||
                     window.location.hostname.startsWith('10.') ||
                     window.location.hostname.startsWith('100.'));

// Never connect to local/private emulators when running on hosted domains (e.g., racelineiq-dev.web.app)
const shouldConnectEmulator = isLocalhost && (
  env.VITE_USE_FIREBASE_EMULATOR === 'true' || 
  ((import.meta.env.DEV || env.MODE === 'development') && env.VITE_USE_FIREBASE_EMULATOR !== 'false')
);

// Connect to Emulators when running in development mode
if (shouldConnectEmulator) {
  const emulatorHost = env.VITE_FIREBASE_EMULATOR_HOST || 'localhost';
  const firestorePort = Number(env.VITE_FIREBASE_FIRESTORE_EMULATOR_PORT) || 8081;
  const storagePort = Number(env.VITE_FIREBASE_STORAGE_EMULATOR_PORT) || 9199;
  const functionsPort = Number(env.VITE_FIREBASE_FUNCTIONS_EMULATOR_PORT) || 5001;
  const authPort = Number(env.VITE_FIREBASE_AUTH_EMULATOR_PORT) || 9099;

  console.log(`[RaceLineIQ] Connecting to Firebase emulators at ${emulatorHost}...`);
  
  try {
    connectFirestoreEmulator(db, emulatorHost, firestorePort);
    connectStorageEmulator(storage, emulatorHost, storagePort);
    connectFunctionsEmulator(functions, emulatorHost, functionsPort);
    auth.useEmulator(`http://${emulatorHost}:${authPort}`);
    
    console.log(`[RaceLineIQ] Connected to emulators at ${emulatorHost} (Firestore:${firestorePort}, Auth:${authPort}, Functions:${functionsPort}, Storage:${storagePort})`);
  } catch (error) {
    console.error("[RaceLineIQ] Error connecting to Firebase emulators:", error);
  }
}